// @ts-nocheck
import { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  Tabs,
  Switch,
  Descriptions,
  Avatar,
  Radio,
  Tooltip,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  ShopOutlined,
  EyeOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EnvironmentOutlined,
  AuditOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { memberAPI, storeAPI } from '../../api'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'

const { Option } = Select

type MemberType = 'individual' | 'group'
type GroupRole = 'BOSS' | 'MANAGER' | 'STAFF'

interface Store {
  id: number
  name: string
  address: string
  province?: string
  city?: string
  district?: string
  detail?: string
  contactName: string
  contactPhone: string
  createdAt?: string
}

interface UserAddress {
  id: number
  userId: number
  name: string
  phone: string
  shopName: string
  province: string
  city: string
  district: string
  detail: string
  isDefault: boolean
}

interface Member {
  id: number
  phone: string
  shopName: string
  memberType: MemberType
  level: string
  avatar?: string
  address?: string
  province?: string
  city?: string
  district?: string
  detail?: string
  groupRole?: GroupRole
  storeCount?: number
  orderCount: number
  totalAmount: number
  status: boolean
  auditStatus?: string
  auditRemark?: string
  createdAt: string
  stores?: Store[]
  contactName?: string
  addresses?: UserAddress[]
}

const levelMap: Record<string, { color: string; text: string }> = {
  A: { color: 'gold', text: 'A级会员' },
  B: { color: 'blue', text: 'B级会员' },
  C: { color: 'green', text: 'C级会员' },
}

const roleMap: Record<GroupRole, { color: string; text: string }> = {
  BOSS: { color: 'red', text: '老板' },
  MANAGER: { color: 'orange', text: '店长' },
  STAFF: { color: 'blue', text: '员工' },
}

const auditStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'orange', text: '待审核' },
  active: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已拒绝' },
}

const regionData = {
  province: ['广东省', '广西壮族自治区', '湖南省', '福建省'],
  city: {
    '广东省': ['广州市', '深圳市', '佛山市', '东莞市', '珠海市'],
    '广西壮族自治区': ['南宁市', '柳州市', '桂林市'],
    '湖南省': ['长沙市', '株洲市', '湘潭市'],
    '福建省': ['福州市', '厦门市', '泉州市'],
  },
  district: {
    '佛山市': ['禅城区', '南海区', '顺德区', '三水区', '高明区'],
    '广州市': ['越秀区', '天河区', '海珠区', '荔湾区', '白云区'],
    '深圳市': ['福田区', '南山区', '罗湖区', '宝安区', '龙岗区'],
    '东莞市': ['莞城区', '东城区', '南城区', '万江区'],
  },
}

const Users = () => {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [storeModalVisible, setStoreModalVisible] = useState(false)
  const [addressModalVisible, setAddressModalVisible] = useState(false)
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false)
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'individual' | 'group'>('all')
  const [filterLevel, setFilterLevel] = useState<string>('all')
  const [filterRole, setFilterRole] = useState<string>('all')
  const [filterAuditStatus, setFilterAuditStatus] = useState<string>('all')
  const [activeTab, setActiveTab] = useState('all')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [form] = Form.useForm()
  const [storeForm] = Form.useForm()
  const [addressForm] = Form.useForm()
  const [passwordForm] = Form.useForm()

  // 审核相关状态
  const [auditModalVisible, setAuditModalVisible] = useState(false)
  const [auditingMember, setAuditingMember] = useState<Member | null>(null)
  const [auditAction, setAuditAction] = useState<'active' | 'rejected'>('active')
  const [auditRemark, setAuditRemark] = useState('')

  const [stats, setStats] = useState({
    totalMembers: 0,
    individualCount: 0,
    groupCount: 0,
    todayNew: 0,
    activeMembers: 0,
    pendingAudit: 0,
  })

  const [allStores, setAllStores] = useState<Store[]>([])
  const [storeManageVisible, setStoreManageVisible] = useState(false)
  const [editingStore, setEditingStore] = useState<Store | null>(null)
  const [storeManageForm] = Form.useForm()
  const [cities, setCities] = useState<string[]>([])
  const [districts, setDistricts] = useState<string[]>([])
  const [storePagination, setStorePagination] = useState({ current: 1, pageSize: 10, total: 0 })

  useEffect(() => {
    fetchMembers()
    fetchStores()
  }, [])

  useEffect(() => {
    fetchMembers()
  }, [filterType, filterLevel, filterRole, filterAuditStatus, searchKeyword, activeTab, pagination.current, pagination.pageSize])

  const fetchMembers = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (searchKeyword) params.keyword = searchKeyword
      if (filterType !== 'all') params.memberType = filterType
      if (filterLevel !== 'all') params.level = filterLevel
      if (filterRole !== 'all') params.role = filterRole

      if (activeTab === 'pending') {
        params.auditStatus = 'pending'
      } else if (activeTab === 'active') {
        params.auditStatus = 'active'
      } else if (activeTab === 'rejected') {
        params.auditStatus = 'rejected'
      } else if (filterAuditStatus !== 'all') {
        params.auditStatus = filterAuditStatus
      }

      const res = await memberAPI.getList(params)
      if (res.code === 200) {
        setMembers(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0 }))
        if (res.data.stats) {
          setStats(res.data.stats)
        } else {
          const list = res.data.list || []
          setStats({
            totalMembers: list.length,
            individualCount: list.filter((m: Member) => m.memberType === 'individual').length,
            groupCount: list.filter((m: Member) => m.memberType === 'group').length,
            todayNew: list.filter((m: Member) => {
              const today = new Date().toISOString().split('T')[0]
              return m.createdAt?.startsWith(today)
            }).length,
            activeMembers: list.filter((m: Member) => m.status).length,
            pendingAudit: list.filter((m: Member) => m.auditStatus === 'pending').length,
          })
        }
      }
    } catch (error) {
      message.error('获取会员失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchStores = async () => {
    try {
      const res = await storeAPI.getList({ page: 1, pageSize: 100 })
      if (res.code === 200) {
        setAllStores(res.data.list || [])
      }
    } catch (error) {
      console.error('获取门店失败:', error)
    }
  }

  const handleAdd = () => {
    setEditingMember(null)
    form.resetFields()
    form.setFieldsValue({ memberType: 'individual', level: 'C', status: true, auditStatus: 'active' })
    setModalVisible(true)
  }

  const handleEdit = (record: Member) => {
    setEditingMember(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleViewDetail = async (record: Member) => {
    try {
      const res = await memberAPI.getDetail(record.id)
      if (res.code === 200) {
        setSelectedMember(res.data)
      } else {
        setSelectedMember(record)
      }
    } catch (error) {
      setSelectedMember(record)
    }
    setDetailVisible(true)
  }

  const handleManageStores = (record: Member) => {
    setSelectedMember(record)
    storeForm.setFieldsValue({
      storeIds: record.stores?.map(s => s.id) || [],
    })
    setStoreModalVisible(true)
  }

  const handleEditAddress = (record: Member) => {
    setSelectedMember(record)

    if (record.province) {
      setCities(regionData.city[record.province as keyof typeof regionData.city] || [])
    }
    if (record.city) {
      setDistricts(regionData.district[record.city as keyof typeof regionData.district] || [])
    }

    addressForm.setFieldsValue({
      province: record.province,
      city: record.city,
      district: record.district,
      detail: record.detail,
    })
    setAddressModalVisible(true)
  }

  const handleResetPassword = (record: Member) => {
    setSelectedMember(record)
    passwordForm.resetFields()
    setResetPasswordVisible(true)
  }

  const handleDelete = (id: number, name: string) => {
    confirmDelete(name, '会员管理', () =>
      memberAPI.delete(id).then(() => {
        setMembers(members.filter(m => m.id !== id))
        message.success('删除成功')
        fetchMembers()
      })
    )
  }

  const handleToggleStatus = (id: number, status: boolean, name: string) => {
    confirmStatusChange(name, status, '会员管理', () =>
      memberAPI.toggleStatus(id, status).then(() => {
        setMembers(members.map(m => (m.id === id ? { ...m, status } : m)))
        message.success(status ? '已启用' : '已禁用')
      })
    )
  }

  const handleLevelChange = async (id: number, level: string) => {
    try {
      await memberAPI.updateLevel(id, level)
      setMembers(members.map(m => (m.id === id ? { ...m, level } : m)))
      message.success('等级更新成功')
    } catch (error) {
      message.error('等级更新失败')
    }
  }

  // 审核相关
  const handleAudit = (record: Member, action: 'active' | 'rejected') => {
    setAuditingMember(record)
    setAuditAction(action)
    setAuditRemark('')
    setAuditModalVisible(true)
  }

  const handleAuditSubmit = async () => {
    if (!auditingMember) return

    if (auditAction === 'rejected' && !auditRemark) {
      message.warning('拒绝时请填写审核备注')
      return
    }

    try {
      await memberAPI.audit(auditingMember.id, {
        status: auditAction,
        auditRemark: auditRemark || (auditAction === 'active' ? '审核通过' : ''),
      })

      setMembers(
        members.map(m =>
          m.id === auditingMember.id
            ? {
                ...m,
                auditStatus: auditAction,
                auditRemark: auditRemark || '审核通过',
              }
            : m
        )
      )
      message.success(auditAction === 'active' ? '审核通过' : '已拒绝')
      setAuditModalVisible(false)
    } catch (error) {
      message.error('审核操作失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      if (values.memberType === 'individual' && values.province) {
        values.address = `${values.province}${values.city}${values.district}${values.detail}`
      }

      if (editingMember) {
        const res = await memberAPI.update(editingMember.id, values)
        if (res.code === 200) {
          setMembers(members.map(m => (m.id === editingMember.id ? { ...m, ...res.data } : m)))
          message.success('更新成功')
        }
      } else {
        const res = await memberAPI.create(values)
        if (res.code === 200) {
          setMembers([res.data, ...members])
          message.success('添加成功')
        }
      }
      setModalVisible(false)
      fetchMembers()
    } catch (error) {
      message.error('操作失败')
    }
  }

  const handleStoreSubmit = async () => {
    if (!selectedMember) return
    try {
      const values = await storeForm.validateFields()
      await memberAPI.updateStores(selectedMember.id, values.storeIds || [])

      const selectedStores = allStores.filter(s => values.storeIds?.includes(s.id))
      setMembers(
        members.map(m => {
          if (m.id === selectedMember.id) {
            return { ...m, stores: selectedStores, storeCount: selectedStores.length }
          }
          return m
        })
      )

      message.success('门店关联成功')
      setStoreModalVisible(false)
    } catch (error) {
      message.error('操作失败')
    }
  }

  const handleAddressSubmit = async () => {
    if (!selectedMember) return
    try {
      const values = await addressForm.validateFields()
      const fullAddress = `${values.province}${values.city}${values.district}${values.detail}`

      await memberAPI.updateAddress(selectedMember.id, {
        ...values,
        address: fullAddress,
      })

      setMembers(
        members.map(m => {
          if (m.id === selectedMember.id) {
            return {
              ...m,
              province: values.province,
              city: values.city,
              district: values.district,
              detail: values.detail,
              address: fullAddress,
            }
          }
          return m
        })
      )

      message.success('地址更新成功')
      setAddressModalVisible(false)
    } catch (error) {
      message.error('地址更新失败')
    }
  }

  const handlePasswordSubmit = async () => {
    if (!selectedMember) return
    try {
      const values = await passwordForm.validateFields()
      await memberAPI.resetPassword(selectedMember.id, values.newPassword)
      message.success('密码重置成功')
      setResetPasswordVisible(false)
    } catch (error) {
      message.error('密码重置失败')
    }
  }

  const handleProvinceChange = (value: string) => {
    addressForm.setFieldsValue({ city: undefined, district: undefined })
    setCities(regionData.city[value as keyof typeof regionData.city] || [])
    setDistricts([])
  }

  const handleCityChange = (value: string) => {
    addressForm.setFieldsValue({ district: undefined })
    setDistricts(regionData.district[value as keyof typeof regionData.district] || [])
  }

  const handleAddStore = () => {
    setEditingStore(null)
    storeManageForm.resetFields()
    setStoreManageVisible(true)
  }

  const handleEditStore = (record: Store) => {
    setEditingStore(record)
    storeManageForm.setFieldsValue(record)
    setStoreManageVisible(true)
  }

  const handleDeleteStore = (id: number, name: string) => {
    confirmDelete(name, '门店管理', () =>
      storeAPI.delete(id).then(() => {
        setAllStores(allStores.filter(s => s.id !== id))
        message.success('删除成功')
      })
    )
  }

  const handleStoreManageSubmit = async () => {
    try {
      const values = await storeManageForm.validateFields()
      const fullAddress = `${values.province}${values.city}${values.district}${values.detail}`

      if (editingStore) {
        const res = await storeAPI.update(editingStore.id, {
          ...values,
          address: fullAddress,
        })
        if (res.code === 200) {
          setAllStores(allStores.map(s => (s.id === editingStore.id ? res.data : s)))
          message.success('更新成功')
        }
      } else {
        const res = await storeAPI.create({
          ...values,
          address: fullAddress,
        })
        if (res.code === 200) {
          setAllStores([...allStores, res.data])
          message.success('添加成功')
        }
      }
      setStoreManageVisible(false)
    } catch (error) {
      message.error('操作失败')
    }
  }

  const columns: ColumnsType<Member> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '头像',
      dataIndex: 'avatar',
      key: 'avatar',
      width: 60,
      render: (val: string) => (
        <Avatar src={val} icon={<UserOutlined />} size={36} />
      ),
    },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: '店铺名称', dataIndex: 'shopName', key: 'shopName', width: 140 },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 100,
      render: (val: string) => val || '-',
    },
    {
      title: '审核状态',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      width: 90,
      render: (val: string) => (
        <Tag color={auditStatusMap[val]?.color}>
          {auditStatusMap[val]?.text || val || '已通过'}
        </Tag>
      ),
    },
    {
      title: '会员类型',
      dataIndex: 'memberType',
      key: 'memberType',
      width: 90,
      render: (val: MemberType) => (
        <Tag color={val === 'individual' ? 'green' : 'blue'}>
          {val === 'individual' ? '单店会员' : '连锁集团'}
        </Tag>
      ),
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 110,
      render: (val: string, record: Member) => (
        <Select
          value={val}
          style={{ width: 110 }}
          size="small"
          onChange={newLevel => handleLevelChange(record.id, newLevel)}
        >
          <Option value="A">
            <Tag color="gold">A级会员</Tag>
          </Option>
          <Option value="B">
            <Tag color="blue">B级会员</Tag>
          </Option>
          <Option value="C">
            <Tag color="green">C级会员</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: '默认收货地址',
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: true,
      render: (val: string, record: Member) => {
        if (!val) return '-'
        const defaultAddr = record.addresses?.find(a => a.isDefault) || record.addresses?.[0]
        const tooltipText = defaultAddr
          ? `${defaultAddr.name} ${defaultAddr.phone}\n${val}`
          : val
        return (
          <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}>
            <EnvironmentOutlined style={{ marginRight: 4, color: '#1890ff' }} />
            <span>{val}</span>
          </Tooltip>
        )
      },
    },
    {
      title: '角色',
      dataIndex: 'groupRole',
      key: 'groupRole',
      width: 80,
      render: (val: GroupRole | undefined, record: Member) => {
        if (record.memberType === 'individual') return '-'
        return (
          <Tag color={roleMap[val!]?.color}>{roleMap[val!]?.text}</Tag>
        )
      },
    },
    {
      title: '启用',
      dataIndex: 'status',
      key: 'status',
      width: 60,
      render: (val: boolean, record: Member) => (
        <Switch
          checked={val}
          size="small"
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<StopOutlined />}
          onChange={checked => handleToggleStatus(record.id, checked, record.shopName)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: 'right' as const,
      render: (_: any, record: Member) => (
        <Space size="small" wrap>
          {record.auditStatus === 'pending' && (
            <>
              <Button
                type="link"
                size="small"
                style={{ color: '#52c41a' }}
                onClick={() => handleAudit(record, 'active')}
              >
                通过
              </Button>
              <Button
                type="link"
                size="small"
                danger
                onClick={() => handleAudit(record, 'rejected')}
              >
                拒绝
              </Button>
            </>
          )}
          {record.auditStatus !== 'pending' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(record)}
              >
                详情
              </Button>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                编辑
              </Button>
              <Button
                type="link"
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={() => handleEditAddress(record)}
              >
                地址
              </Button>
              {record.memberType === 'group' && (
                <Button
                  type="link"
                  size="small"
                  icon={<ShopOutlined />}
                  onClick={() => handleManageStores(record)}
                >
                  门店
                </Button>
              )}
              <Button
                type="link"
                size="small"
                icon={<KeyOutlined />}
                onClick={() => handleResetPassword(record)}
              >
                重置
              </Button>
              <Popconfirm
                title="确定删除吗？"
                onConfirm={() => handleDelete(record.id, record.shopName)}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ]

  const storeColumns: ColumnsType<Store> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '门店名称', dataIndex: 'name', key: 'name', width: 160 },
    { title: '地址', dataIndex: 'address', key: 'address', width: 220, ellipsis: true },
    { title: '联系人', dataIndex: 'contactName', key: 'contactName', width: 100 },
    { title: '联系电话', dataIndex: 'contactPhone', key: 'contactPhone', width: 130 },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 110 },
    {
      title: '操作',
      key: 'action',
      width: 130,
      render: (_: any, record: Store) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStore(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDeleteStore(record.id, record.name)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="users-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title="会员总数" value={stats.totalMembers} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="单店会员"
              value={stats.individualCount}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="连锁集团"
              value={stats.groupCount}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="待审核"
              value={stats.pendingAudit}
              prefix={<AuditOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="今日新增" value={stats.todayNew} valueStyle={{ color: '#006835' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="门店总数" value={allStores.length} prefix={<ShopOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="全部会员" key="all" />
          <Tabs.TabPane tab="待审核" key="pending" />
          <Tabs.TabPane tab="已通过" key="active" />
          <Tabs.TabPane tab="已拒绝" key="rejected" />
        </Tabs>

        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索手机号/店铺名"
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onPressEnter={fetchMembers}
          />
          <Select style={{ width: 110 }} value={filterType} onChange={setFilterType}>
            <Option value="all">全部类型</Option>
            <Option value="individual">单店会员</Option>
            <Option value="group">连锁集团</Option>
          </Select>
          <Select style={{ width: 110 }} value={filterLevel} onChange={setFilterLevel}>
            <Option value="all">全部等级</Option>
            <Option value="A">A级会员</Option>
            <Option value="B">B级会员</Option>
            <Option value="C">C级会员</Option>
          </Select>
          <Select style={{ width: 110 }} value={filterRole} onChange={setFilterRole}>
            <Option value="all">全部角色</Option>
            <Option value="BOSS">老板</Option>
            <Option value="MANAGER">店长</Option>
            <Option value="STAFF">员工</Option>
          </Select>
          <Select style={{ width: 110 }} value={filterAuditStatus} onChange={setFilterAuditStatus}>
            <Option value="all">全部审核</Option>
            <Option value="pending">待审核</Option>
            <Option value="active">已通过</Option>
            <Option value="rejected">已拒绝</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchMembers}>
            搜索
          </Button>
        </Space>

        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加会员
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: total => `共 ${total} 条`,
            onChange: (page, pageSize) =>
              setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>

      {/* 审核弹窗 */}
      <Modal
        title={auditAction === 'active' ? '确认通过审核' : '确认拒绝'}
        open={auditModalVisible}
        onOk={handleAuditSubmit}
        onCancel={() => setAuditModalVisible(false)}
        okText={auditAction === 'active' ? '确认通过' : '确认拒绝'}
        okType={auditAction === 'active' ? 'primary' : 'danger'}
        width={400}
      >
        <p>
          {auditAction === 'active'
            ? `确认通过「${auditingMember?.shopName}」的注册申请？`
            : `确认拒绝「${auditingMember?.shopName}」的注册申请？`}
        </p>
        <p>手机号：{auditingMember?.phone}</p>
        {auditAction === 'rejected' && (
          <Form.Item label="拒绝原因" required>
            <Input.TextArea
              rows={3}
              placeholder="请输入拒绝原因"
              value={auditRemark}
              onChange={e => setAuditRemark(e.target.value)}
            />
          </Form.Item>
        )}
      </Modal>

      {/* 添加/编辑会员弹窗 */}
      <Modal
        title={editingMember ? '编辑会员' : '添加会员'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="memberType" label="会员类型" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="individual">单店会员</Radio>
              <Radio value="group">连锁集团</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input placeholder="请输入手机号" disabled={!!editingMember} />
          </Form.Item>

          <Form.Item
            name="shopName"
            label="店铺名称"
            rules={[{ required: true, message: '请输入店铺名称' }]}
          >
            <Input placeholder="请输入店铺名称" />
          </Form.Item>

          <Form.Item name="contactName" label="联系人">
            <Input placeholder="请输入联系人姓名" />
          </Form.Item>

          <Form.Item name="level" label="会员等级" rules={[{ required: true }]}>
            <Select placeholder="请选择会员等级">
              <Option value="A">A级会员（最优价格）</Option>
              <Option value="B">B级会员（折扣价格）</Option>
              <Option value="C">C级会员（基础价格）</Option>
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.memberType !== curr.memberType}>
            {({ getFieldValue }) => {
              const memberType = getFieldValue('memberType')

              if (memberType === 'group') {
                return (
                  <Form.Item
                    name="groupRole"
                    label="集团角色"
                    rules={[{ required: true }]}
                  >
                    <Select placeholder="请选择角色">
                      <Option value="BOSS">老板（可管理所有门店）</Option>
                      <Option value="MANAGER">店长（管理指定门店）</Option>
                    </Select>
                  </Form.Item>
                )
              }

              return null
            }}
          </Form.Item>

          <Form.Item name="status" label="状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          {!editingMember && (
            <Form.Item
              name="password"
              label="初始密码"
              rules={[
                { required: true, message: '请输入初始密码' },
                { min: 6, message: '密码至少6位' },
              ]}
            >
              <Input.Password placeholder="请输入初始密码" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 会员详情弹窗 */}
      <Modal
        title="会员详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {selectedMember && (
          <div>
            <Row gutter={16}>
              <Col span={8} style={{ textAlign: 'center' }}>
                <Avatar
                  src={selectedMember.avatar}
                  icon={<UserOutlined />}
                  size={100}
                />
                <h3 style={{ marginTop: 12 }}>{selectedMember.shopName}</h3>
                <Tag
                  color={selectedMember.memberType === 'individual' ? 'green' : 'blue'}
                >
                  {selectedMember.memberType === 'individual'
                    ? '单店会员'
                    : '连锁集团'}
                </Tag>
                <Tag color={levelMap[selectedMember.level]?.color}>
                  {levelMap[selectedMember.level]?.text}
                </Tag>
                {selectedMember.auditStatus && (
                  <Tag color={auditStatusMap[selectedMember.auditStatus]?.color}>
                    {auditStatusMap[selectedMember.auditStatus]?.text}
                  </Tag>
                )}
              </Col>
              <Col span={16}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="会员ID">{selectedMember.id}</Descriptions.Item>
                  <Descriptions.Item label="手机号">{selectedMember.phone}</Descriptions.Item>
                  <Descriptions.Item label="联系人">{selectedMember.contactName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="注册时间">
                    {selectedMember.createdAt}
                  </Descriptions.Item>
                  <Descriptions.Item label="状态">
                    <Tag color={selectedMember.status ? 'green' : 'red'}>
                      {selectedMember.status ? '启用' : '禁用'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="店铺地址" span={2}>
                    {selectedMember.address || '-'}
                  </Descriptions.Item>
                  {selectedMember.memberType === 'group' && (
                    <>
                      <Descriptions.Item label="集团角色">
                        <Tag color={roleMap[selectedMember.groupRole!]?.color}>
                          {roleMap[selectedMember.groupRole!]?.text}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="关联门店">
                        {selectedMember.storeCount || 0} 家
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Col>
            </Row>

            <hr style={{ margin: '20px 0' }} />

            <h4>收货地址（{selectedMember.addresses?.length || 0} 条）</h4>
            {selectedMember.addresses && selectedMember.addresses.length > 0 ? (
              <Table
                dataSource={selectedMember.addresses}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: '收货人', dataIndex: 'name', key: 'name', width: 100 },
                  { title: '联系电话', dataIndex: 'phone', key: 'phone', width: 120 },
                  {
                    title: '地址',
                    key: 'fullAddress',
                    render: (_: any, r: UserAddress) =>
                      `${r.province}${r.city}${r.district}${r.detail}`,
                  },
                  {
                    title: '默认',
                    dataIndex: 'isDefault',
                    key: 'isDefault',
                    width: 70,
                    render: (val: boolean) =>
                      val ? <Tag color="green">默认</Tag> : null,
                  },
                ]}
              />
            ) : (
              <div style={{ color: '#999', marginBottom: 16 }}>该用户暂未填写收货地址</div>
            )}

            <h4 style={{ marginTop: 20 }}>消费统计</h4>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="累计订单" value={selectedMember.orderCount} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="消费总额"
                  value={selectedMember.totalAmount}
                  precision={2}
                  prefix="¥"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="客单价"
                  value={
                    selectedMember.orderCount > 0
                      ? selectedMember.totalAmount / selectedMember.orderCount
                      : 0
                  }
                  precision={2}
                  prefix="¥"
                />
              </Col>
            </Row>

            {selectedMember.memberType === 'group' && selectedMember.stores && (
              <>
                <h4 style={{ marginTop: 20 }}>关联门店</h4>
                <Table
                  dataSource={selectedMember.stores}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '门店名称', dataIndex: 'name', key: 'name' },
                    { title: '地址', dataIndex: 'address', key: 'address' },
                    { title: '联系人', dataIndex: 'contactName', key: 'contactName' },
                    { title: '联系电话', dataIndex: 'contactPhone', key: 'contactPhone' },
                  ]}
                />
              </>
            )}
          </div>
        )}
      </Modal>

      {/* 编辑地址弹窗 */}
      <Modal
        title={`编辑地址 - ${selectedMember?.shopName}`}
        open={addressModalVisible}
        onOk={handleAddressSubmit}
        onCancel={() => setAddressModalVisible(false)}
        width={500}
        destroyOnClose
      >
        <Form form={addressForm} layout="vertical">
          <Form.Item
            name="province"
            label="省份"
            rules={[{ required: true, message: '请选择省份' }]}
          >
            <Select placeholder="请选择省份" onChange={handleProvinceChange}>
              {regionData.province.map(p => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="city"
            label="城市"
            rules={[{ required: true, message: '请选择城市' }]}
          >
            <Select
              placeholder="请选择城市"
              onChange={handleCityChange}
              disabled={!cities.length}
            >
              {cities.map(c => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="district"
            label="区/县"
            rules={[{ required: true, message: '请选择区/县' }]}
          >
            <Select placeholder="请选择区/县" disabled={!districts.length}>
              {districts.map(d => (
                <Option key={d} value={d}>
                  {d}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="detail"
            label="详细地址"
            rules={[{ required: true, message: '请输入详细地址' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入详细地址" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 管理门店弹窗 */}
      <Modal
        title={`管理门店 - ${selectedMember?.shopName}`}
        open={storeModalVisible}
        onOk={handleStoreSubmit}
        onCancel={() => setStoreModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={storeForm} layout="vertical">
          <Form.Item name="storeIds" label="选择关联门店">
            <Select
              mode="multiple"
              placeholder="请选择关联的门店"
              style={{ width: '100%' }}
            >
              {allStores.map(store => (
                <Option key={store.id} value={store.id}>
                  {store.name} ({store.address})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ fontSize: 12, color: '#999' }}>
            提示：老板可以管理多个门店，店长和员工通常只关联一个门店
          </div>
        </Form>
      </Modal>

      {/* 重置密码弹窗 */}
      <Modal
        title={`重置密码 - ${selectedMember?.shopName}`}
        open={resetPasswordVisible}
        onOk={handlePasswordSubmit}
        onCancel={() => setResetPasswordVisible(false)}
        width={400}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加/编辑门店弹窗 */}
      <Modal
        title={editingStore ? '编辑门店' : '添加门店'}
        open={storeManageVisible}
        onOk={handleStoreManageSubmit}
        onCancel={() => setStoreManageVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={storeManageForm} layout="vertical">
          <Form.Item
            name="name"
            label="门店名称"
            rules={[{ required: true, message: '请输入门店名称' }]}
          >
            <Input placeholder="请输入门店名称" />
          </Form.Item>

          <Form.Item
            name="province"
            label="省份"
            rules={[{ required: true, message: '请选择省份' }]}
          >
            <Select placeholder="请选择省份" onChange={handleProvinceChange}>
              {regionData.province.map(p => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="city"
            label="城市"
            rules={[{ required: true, message: '请选择城市' }]}
          >
            <Select
              placeholder="请选择城市"
              onChange={handleCityChange}
              disabled={!cities.length}
            >
              {cities.map(c => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="district"
            label="区/县"
            rules={[{ required: true, message: '请选择区/县' }]}
          >
            <Select placeholder="请选择区/县" disabled={!districts.length}>
              {districts.map(d => (
                <Option key={d} value={d}>
                  {d}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="detail"
            label="详细地址"
            rules={[{ required: true, message: '请输入详细地址' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入详细地址" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contactName"
                label="联系人"
                rules={[{ required: true, message: '请输入联系人' }]}
              >
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPhone"
                label="联系电话"
                rules={[
                  { required: true, message: '请输入联系电话' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
                ]}
              >
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default Users
